import { PHASE } from "../../../common/constants.ts";
import { finances, season, team } from "../core/index.ts";
import { idb } from "../db/index.ts";
import { g, helpers, orderTeams } from "../util/index.ts";
import type { Player, UpdateEvents, ViewInput, TeamSeasonAttr } from "../../../common/types.ts";
import { addMood } from "./freeAgents.ts";
import addFirstNameShort from "../util/addFirstNameShort.ts";
import { getActualPlayThroughInjuries } from "../core/game/loadTeams.ts";
import { getHistoryTeam } from "./teamHistoryShared.ts";
import { getPlayoffsByConfBySeason } from "./teamSeasonsShared.ts";
import updateTeamFinances from "./teamFinances.ts";

// N3-C: the opponent team-history page is low-value on its own, so instead of
// a standalone destination the franchise's all-time record / best season /
// title count is folded into a one-line summary at the top of the opponent's
// roster. Computed only when scouting another team's current roster.
const getRosterTeamHistorySummary = async (tid: number) => {
  const teamSeasons = await idb.getCopies.teamSeasons({ tid }, "noCopyCache");
  const playoffsByConfBySeason = await getPlayoffsByConfBySeason();
  const h = getHistoryTeam(teamSeasons, playoffsByConfBySeason);

  return {
    totalWon: h.totalWon,
    totalLost: h.totalLost,
    totalTied: h.totalTied,
    totalOtl: h.totalOtl,
    championships: h.championships,
    playoffAppearances: h.playoffAppearances,
    finalsAppearances: h.finalsAppearances,
    bestRecord: h.bestRecord
      ? {
          season: h.bestRecord.season,
          won: h.bestRecord.won,
          lost: h.bestRecord.lost,
          tied: h.bestRecord.tied,
          otl: h.bestRecord.otl,
        }
      : undefined,
  };
};

const getStandingsInfo = async (info: { season: number; tid: number }) => {
  const teams = await idb.getCopies.teamsPlus(
    {
      attrs: ["tid"],
      seasonAttrs: [
        "won",
        "lost",
        "tied",
        "otl",
        "wonDiv",
        "lostDiv",
        "tiedDiv",
        "otlDiv",
        "wonConf",
        "lostConf",
        "tiedConf",
        "otlConf",
        "winp",
        "pts",
        "cid",
        "did",
      ],
      stats: ["pts", "oppPts", "gp"],
      season: info.season,
      showNoStats: true,
    },
    "noCopyCache",
  );

  const cid = teams.find((t) => t.tid === info.tid)?.seasonAttrs.cid;

  const playoffsByConf = await season.getPlayoffsByConf(info.season);

  const confOrAllTeams = await orderTeams(
    teams.filter((t) => !playoffsByConf || t.seasonAttrs.cid === cid),
    teams,
  );

  const pointsFormula = g.get("pointsFormula", info.season);
  const usePts = pointsFormula !== "";

  const firstPlaceTeam = confOrAllTeams[0];

  if (firstPlaceTeam) {
    let rank = 1;
    for (const t of confOrAllTeams) {
      if (!playoffsByConf || t.seasonAttrs.cid === cid) {
        if (t.tid === info.tid) {
          return {
            gb: usePts
              ? firstPlaceTeam.seasonAttrs.pts - t.seasonAttrs.pts
              : helpers.gb(firstPlaceTeam.seasonAttrs, t.seasonAttrs),
            playoffsByConf,
            rank,
            usePts,
          };
        }

        rank += 1;
      }
    }
  }

  return {
    gb: 0,
    playoffsByConf,
    rank: undefined,
    usePts,
  };
};

const updateRoster = async (
  inputs: ViewInput<"roster">,
  updateEvents: UpdateEvents,
  state: any,
) => {
  if (
    updateEvents.includes("firstRun") ||
    updateEvents.includes("gameAttributes") ||
    updateEvents.includes("playerMovement") ||
    updateEvents.includes("teamFinances") ||
    updateEvents.includes("team") ||
    (inputs.season === g.get("season") &&
      (updateEvents.includes("gameSim") || updateEvents.includes("newPhase"))) ||
    (updateEvents.includes("newPhase") && g.get("phase") === PHASE.PRESEASON) ||
    inputs.abbrev !== state.abbrev ||
    inputs.playoffs !== state.playoffs ||
    inputs.season !== state.season
  ) {
    const stats = ["gp", "min", "pts", "trb", "ast", "per"];

    const editable =
      inputs.season === g.get("season") && inputs.tid === g.get("userTid") && !g.get("spectator");

    const showRelease =
      inputs.season === g.get("season") && inputs.tid === g.get("userTid") && !g.get("spectator");

    const seasonAttrs: TeamSeasonAttr[] = [
      "profit",
      "won",
      "lost",
      "tied",
      "otl",
      "playoffRoundsWon",
      "imgURL",
      "region",
      "name",
      "avgAge",
    ];
    const t = await idb.getCopy.teamsPlus(
      {
        season: inputs.season,
        tid: inputs.tid,
        // colors/jersey: C2 team-color hero tint + starter face-thumb jerseys
        attrs: [
          "tid",
          "strategy",
          "region",
          "name",
          "keepRosterSorted",
          "playThroughInjuries",
          "colors",
          "jersey",
        ],
        seasonAttrs,
        stats: ["pts", "oppPts", "gp"],
        addDummySeason: true,
      },
      "noCopyCache",
    );

    if (!t) {
      const returnValue = {
        errorMessage: "Invalid team ID.",
      };
      return returnValue;
    }

    const attrs = [
      "pid",
      "tid",
      "draft",
      "firstName",
      "lastName",
      "age",
      "born",
      "contract",
      "cashOwed",
      "rosterOrder",
      "injury",
      "ptModifier",
      "watch",
      "untradable",
      "hof",
      "latestTransaction",
      "mood",
      "value",
      "awards",
      // C2: starting-five face thumbnails
      "face",
      "imgURL",
    ]; // tid and draft are used for checking if a player can be released without paying his salary

    const ratings = ["ovr", "pot", "dovr", "dpot", "skills", "pos", "ovrs"];
    const stats2 = [...stats, "yearsWithTeam", "jerseyNumber", "min", "gp"];

    let players: any[];
    let payroll: number | undefined;
    let luxuryTaxAmount: number | undefined;
    let minPayrollAmount: number | undefined;

    if (inputs.season === g.get("season")) {
      const schedule = await season.getSchedule();

      // Show players currently on the roster
      const playersAll = await addMood(
        await idb.cache.players.indexGetAll("playersByTid", inputs.tid),
      );
      payroll = await team.getPayroll(inputs.tid);
      luxuryTaxAmount = finances.getLuxuryTaxAmount(payroll) / 1000;
      minPayrollAmount = finances.getMinPayrollAmount(payroll) / 1000;
      payroll /= 1000;

      // numGamesRemaining doesn't need to be calculated except for userTid, but it is.
      let numGamesRemaining = 0;

      for (const matchup of schedule) {
        if (inputs.tid === matchup.homeTid || inputs.tid === matchup.awayTid) {
          numGamesRemaining += 1;
        }
      }

      players = await idb.getCopies.playersPlus(playersAll, {
        attrs,
        ratings,
        playoffs: inputs.playoffs === "playoffs",
        regularSeason: inputs.playoffs === "regularSeason",
        combined: inputs.playoffs === "combined",
        stats: stats2,
        season: inputs.season,
        tid: inputs.tid,
        showNoStats: true,
        showRookies: true,
        fuzz: true,
        numGamesRemaining,
      });

      players.sort((a, b) => a.rosterOrder - b.rosterOrder);

      for (const p of players) {
        // Can alway release player, even if below the minimum roster limit, cause why not. Except in the playoffs.
        if (
          inputs.tid === g.get("userTid") &&
          (g.get("phase") !== PHASE.PLAYOFFS ||
            (g.get("phase") === PHASE.PLAYOFFS && players.length > g.get("minRosterSize"))) &&
          !g.get("gameOver") &&
          !g.get("otherTeamsWantToHire") &&
          g.get("phase") !== PHASE.FANTASY_DRAFT &&
          g.get("phase") !== PHASE.EXPANSION_DRAFT
        ) {
          p.canRelease = true;
        } else {
          p.canRelease = false;
        }

        // Convert ptModifier to string so it doesn't cause unneeded knockout re-rendering
        p.ptModifier = String(p.ptModifier);
      }
    } else {
      // Show all players with stats for the given team and year
      const playersAll = await idb.getCopies.players(
        {
          activeSeason: inputs.season,
          statsTid: inputs.tid,
        },
        "noCopyCache",
      );
      players = await idb.getCopies.playersPlus(playersAll, {
        attrs,
        ratings,
        playoffs: inputs.playoffs === "playoffs",
        regularSeason: inputs.playoffs === "regularSeason",
        combined: inputs.playoffs === "combined",
        stats: stats2,
        season: inputs.season,
        tid: inputs.tid,
        fuzz: true,
      });

      players.sort((a, b) => b.stats.gp * b.stats.min - a.stats.gp * a.stats.min);

      for (const p of players) {
        p.canRelease = false;
      }

      const teamSeason = await idb.getCopy.teamSeasons({
        season: inputs.season,
        tid: inputs.tid,
      });

      // >0 check handles old leagues that might have it undefined, and real players leagues that have a dummy negative value
      if (teamSeason && teamSeason.payrollEndOfSeason > 0) {
        payroll = teamSeason.payrollEndOfSeason / 1000;
        luxuryTaxAmount = teamSeason.expenses.luxuryTax / 1000;
        minPayrollAmount = teamSeason.expenses.minTax / 1000;
      }
    }

    const playoffsOvr =
      (g.get("phase") === PHASE.PLAYOFFS && g.get("season") === inputs.season) ||
      inputs.playoffs === "playoffs";

    const { gb, playoffsByConf, rank, usePts } = await getStandingsInfo(inputs);

    const t2 = {
      ...t,
      ovr: team.ovr(players, {
        playoffs: playoffsOvr,
      }),
      ovrCurrent: team.ovr(players, {
        accountForInjuredPlayers: {
          numDaysInFuture: 0,
          playThroughInjuries: getActualPlayThroughInjuries(t),
        },
        playoffs: playoffsOvr,
      }),
      roundsWonText: helpers.roundsWonText({
        playoffRoundsWon: t.seasonAttrs.playoffRoundsWon,
        numPlayoffRounds: g.get("numGamesPlayoffSeries", inputs.season).length,
        playoffsByConf: await season.getPlayoffsByConf(inputs.season),
      }),
      gb,
      rank,
    };
    t2.seasonAttrs.avgAge = t2.seasonAttrs.avgAge ?? team.avgAge(players);

    for (const p of players) {
      p.awards = p.awards.filter(
        (award: Player["awards"][number]) => award.season === inputs.season,
      );
    }

    const teamHistorySummary =
      inputs.tid !== g.get("userTid") && inputs.season === g.get("season")
        ? await getRosterTeamHistorySummary(inputs.tid)
        : undefined;

    // The user's current Team tab owns the mobile finance workspace. Reuse the
    // canonical team-finances view payload so its sheets and the direct URL can
    // never drift into two different payroll/contract/budget calculations.
    const teamFinances =
      inputs.tid === g.get("userTid") && inputs.season === g.get("season")
        ? await updateTeamFinances(
            {
              abbrev: inputs.abbrev,
              show: "10",
              tid: inputs.tid,
            },
            ["firstRun"],
            {},
          )
        : undefined;

    return {
      abbrev: inputs.abbrev,
      budget: g.get("budget"),
      challengeNoRatings: g.get("challengeNoRatings"),
      currentSeason: g.get("season"),
      editable,
      salaryCapType: g.get("salaryCapType"),
      maxRosterSize: g.get("maxRosterSize"),
      minRosterSize: g.get("minRosterSize"),
      numPlayersOnCourt: g.get("numPlayersOnCourt"),
      luxuryPayroll: g.get("luxuryPayroll") / 1000,
      luxuryTaxAmount,
      minPayroll: g.get("minPayroll") / 1000,
      minPayrollAmount,
      payroll,
      phase: g.get("phase"),
      playoffs: inputs.playoffs,
      playoffsByConf,
      players: addFirstNameShort(players),
      salaryCap: g.get("salaryCap") / 1000,
      season: inputs.season,
      showSpectatorWarning:
        inputs.season === g.get("season") && inputs.tid === g.get("userTid") && g.get("spectator"),
      showRelease,
      showTradeFor:
        inputs.season === g.get("season") && inputs.tid !== g.get("userTid") && !g.get("spectator"),
      showPlayThroughInjuries:
        inputs.season === g.get("season") && inputs.tid === g.get("userTid") && !g.get("spectator"),
      stats,
      t: t2,
      teamHistorySummary,
      teamFinances,
      tid: inputs.tid,
      usePts,
      userTid: g.get("userTid"),
    };
  }
};

// Shared read-only roster payload for cross-domain sheets. Main tabs never need
// to navigate into the Team tab just to inspect an opponent; they all consume
// this same canonical roster calculation instead.
export const getTeamRosterSheet = async ({ season, tid }: { season?: number; tid: number }) => {
  const targetSeason = season ?? g.get("season");
  const teamRecord = await idb.cache.teams.get(tid);
  if (!teamRecord) {
    throw new Error("Team not found");
  }

  const data = await updateRoster(
    {
      abbrev: teamRecord.abbrev,
      playoffs: "regularSeason",
      season: targetSeason,
      tid,
    },
    ["firstRun"],
    {},
  );

  if (!data || "errorMessage" in data) {
    throw new Error(data?.errorMessage ?? "Roster unavailable");
  }

  return {
    challengeNoRatings: data.challengeNoRatings,
    players: data.players.map((p) => ({
      contract: p.contract,
      firstName: p.firstName,
      firstNameShort: p.firstNameShort,
      injury: p.injury,
      lastName: p.lastName,
      pid: p.pid,
      pos: p.ratings.pos,
      pot: p.ratings.pot,
      ovr: p.ratings.ovr,
    })),
    season: targetSeason,
    team: {
      abbrev: data.abbrev,
      name: data.t.name,
      region: data.t.region,
      tid,
    },
  };
};

export default updateRoster;
